/**
 * WaveSurferPlayer - Core reusable waveform audio player
 *
 * Features:
 * - WaveSurfer.js integration for waveform visualization
 * - Region support for flashcard segments
 * - Timestamp conversion (milliseconds → seconds)
 * - Terminal cyberpunk theme styling
 * - Playback controls (play/pause, seek, volume, loop, speed: 0.8x-1.2x)
 * - Region click callbacks for navigation
 * - Smart playback buffers (500ms pre-roll, 500ms tail)
 * - Automatic fade in/out when looping (500ms fade-in, 500ms fade-out)
 *
 * Playback Behavior:
 * - Visual regions display exact linguistic boundaries
 * - Playback includes 500ms pre-roll and 500ms tail for natural flow
 * - Buffers only applied when clip isn't at song start/end
 * - Loop mode enables smooth fade in/out over buffer periods
 *
 * Usage:
 * <WaveSurferPlayer
 *   audioUrl="https://..."
 *   regions={[{id: '1', start: 240, end: 640, text: 'Smoke', color: '#00ff00'}]}
 *   onRegionClick={(region) => console.log('Clicked:', region)}
 * />
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import WaveSurfer from 'wavesurfer.js'
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions.js'
import { Repeat1, X, ChevronLeft, ChevronRight, Square, Music } from 'lucide-react'

/**
 * Region data structure for flashcard segments
 * Timestamps in MILLISECONDS (AssemblyAI format)
 */
export interface FlashcardRegion {
  /** Unique identifier */
  id: string
  /** Start time in MILLISECONDS */
  start: number
  /** End time in MILLISECONDS */
  end: number
  /** Text content (word or phrase) */
  text: string
  /** Region color (hex code) */
  color?: string
  /** Review score (0-5) for color-coding */
  reviewScore?: number
}

interface WaveSurferPlayerProps {
  /** Audio file URL */
  audioUrl: string
  /** Flashcard regions (timestamps in MILLISECONDS) */
  regions?: FlashcardRegion[]
  /** Callback when region is clicked */
  onRegionClick?: (region: FlashcardRegion) => void
  /** Callback when playback position changes */
  onTimeUpdate?: (currentTime: number) => void
  /** Height of waveform in pixels */
  height?: number
  /** Optional CSS class */
  className?: string
  /** Whole song mode: disables region-based playback boundaries */
  wholeSongMode?: boolean
  /** Callback when playback mode toggle is clicked (for cycling compatibility) */
  onPlaybackModeToggle?: () => void
  /** Optional: Playback mode for enhanced UI (vocab/wholeSong) */
  playbackMode?: 'vocab' | 'wholeSong'
  /** Callback when a specific mode is selected */
  onModeSelect?: (mode: 'vocab' | 'wholeSong') => void
}

/**
 * Convert milliseconds to seconds for WaveSurfer.js
 */
const msToSeconds = (ms: number): number => ms / 1000

/**
 * Get region color based on review score (0-5)
 * - 0-1: Red (blackout/incorrect)
 * - 2-3: Yellow (partial/hesitation)
 * - 4-5: Green (correct/perfect)
 * - No score: Cyan (default)
 */
const getRegionColor = (reviewScore?: number): string => {
  if (reviewScore === undefined) return '#00ffff' // Cyan default
  if (reviewScore <= 1) return '#ff0080' // Hot pink for errors
  if (reviewScore <= 3) return '#ffff00' // Yellow for partial
  return '#00ff00' // Neon green for correct
}

export function WaveSurferPlayer({
  audioUrl,
  regions = [],
  onRegionClick,
  onTimeUpdate,
  height = 128,
  className = '',
  wholeSongMode = false,
  onPlaybackModeToggle,
  playbackMode,
  onModeSelect,
}: WaveSurferPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const regionsPluginRef = useRef<RegionsPlugin | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [loopMode, setLoopMode] = useState<'none' | 'single'>('none')
  const [playbackRate, setPlaybackRate] = useState(1.0)
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false)
  const currentRegionRef = useRef<Region | null>(null)

  // Initialize WaveSurfer
  useEffect(() => {
    if (!containerRef.current) return

    // Create regions plugin
    const regionsPlugin = RegionsPlugin.create()
    regionsPluginRef.current = regionsPlugin

    // Initialize WaveSurfer with terminal theme colors
    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#ffffff',      // White
      progressColor: '#00aa00',   // Darker green
      cursorColor: '#ff0080',     // Hot pink
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      height,
      normalize: true,
      plugins: [regionsPlugin],
    })

    wavesurferRef.current = wavesurfer

    // Event listeners
    wavesurfer.on('ready', () => {
      setIsReady(true)
      setDuration(wavesurfer.getDuration())
      console.log('Audio ready, duration:', wavesurfer.getDuration())
    })

    wavesurfer.on('play', () => setIsPlaying(true))
    wavesurfer.on('pause', () => setIsPlaying(false))

    wavesurfer.on('timeupdate', (time) => {
      setCurrentTime(time)
      onTimeUpdate?.(time)
    })

    // Load audio
    wavesurfer.load(audioUrl)

    // Cleanup
    return () => {
      wavesurfer.destroy()
    }
  }, [audioUrl, height, onTimeUpdate])

  // Update regions when they change
  useEffect(() => {
    if (!regionsPluginRef.current || !isReady) return

    // Clear existing regions
    regionsPluginRef.current.clearRegions()

    // Get audio duration for buffer calculation
    const audioDuration = wavesurferRef.current?.getDuration() || 0

    // Add new regions with timestamp conversion
    regions.forEach((region) => {
      const color = region.color || getRegionColor(region.reviewScore)
      const regionStartSeconds = msToSeconds(region.start)
      const regionEndSeconds = msToSeconds(region.end)

      // Add buffers for natural playback: 500ms before, 500ms after
      // Visual region shows exact linguistic boundaries
      // Playback includes buffer for natural audio flow and fade in/out
      const START_BUFFER_MS = 500
      const END_BUFFER_MS = 500
      const startBufferSec = START_BUFFER_MS / 1000
      const endBufferSec = END_BUFFER_MS / 1000

      // Buffer at start: only if clip doesn't start at song beginning
      const hasStartBuffer = regionStartSeconds > startBufferSec
      const bufferedStartSec: number = hasStartBuffer ? regionStartSeconds - startBufferSec : 0

      // Buffer at end: only if clip doesn't end at song end
      const hasEndBuffer = audioDuration > 0 && (regionEndSeconds + endBufferSec) < audioDuration
      const bufferedEndSec: number = hasEndBuffer ? regionEndSeconds + endBufferSec : regionEndSeconds

      const wsRegion = regionsPluginRef.current?.addRegion({
        id: region.id,
        start: regionStartSeconds,  // Visual region start (no buffer)
        end: regionEndSeconds,      // Visual region end (no buffer)
        color: `${color}33`,        // Add transparency (20%)
        drag: false,
        resize: false,
      })

      // Store the actual playback times (with buffers) for seek/pause logic
      if (wsRegion) {
        const regionWithMeta = wsRegion as any
        regionWithMeta._playbackStartTime = bufferedStartSec
        regionWithMeta._playbackEndTime = bufferedEndSec
        regionWithMeta._hasStartBuffer = hasStartBuffer
        regionWithMeta._hasEndBuffer = hasEndBuffer
        regionWithMeta._fadeInDuration = hasStartBuffer ? startBufferSec : 0
        regionWithMeta._fadeOutDuration = hasEndBuffer ? endBufferSec : 0
      }

      // If this is the first/only region, store it and seek to buffered start
      if (regions.length === 1 && wsRegion) {
        currentRegionRef.current = wsRegion
        // Auto-seek to buffered start time (includes 500ms pre-roll)
        wavesurferRef.current?.setTime(bufferedStartSec)
      }
    })

    // Handle region clicks
    regionsPluginRef.current.on('region-clicked', (region: Region) => {
      const flashcardRegion = regions.find((r) => r.id === region.id)
      if (flashcardRegion) {
        onRegionClick?.(flashcardRegion)
        currentRegionRef.current = region

        // Seek to buffered start time and play (includes 500ms pre-roll)
        const playbackStartTime = (region as any)._playbackStartTime || region.start
        wavesurferRef.current?.setTime(playbackStartTime)
        wavesurferRef.current?.play()
      }
    })

    // Handle buffered pause/loop via timeupdate
    const handleTimeUpdate = (time: number) => {
      if (!currentRegionRef.current || !wavesurferRef.current?.isPlaying()) return

      // Skip boundary enforcement in whole song mode
      if (wholeSongMode) return

      const region = currentRegionRef.current as any
      const playbackEndTime = region._playbackEndTime
      const playbackStartTime = region._playbackStartTime
      const fadeInDuration = region._fadeInDuration || 0
      const fadeOutDuration = region._fadeOutDuration || 0
      const hasStartBuffer = region._hasStartBuffer || false
      const hasEndBuffer = region._hasEndBuffer || false

      // Apply fade in/out when looping and buffers are present
      if (loopMode === 'single') {
        let volume = 1.0

        // Fade in: from playback start to start + fade duration
        if (hasStartBuffer && fadeInDuration > 0 && time < playbackStartTime + fadeInDuration) {
          const fadeProgress = (time - playbackStartTime) / fadeInDuration
          volume = Math.max(0, Math.min(1, fadeProgress))
        }
        // Fade out: from end - fade duration to end
        else if (hasEndBuffer && fadeOutDuration > 0 && playbackEndTime && time > playbackEndTime - fadeOutDuration) {
          const fadeProgress = (playbackEndTime - time) / fadeOutDuration
          volume = Math.max(0, Math.min(1, fadeProgress))
        }

        wavesurferRef.current?.setVolume(volume)
      } else {
        // Reset volume to full when not looping
        wavesurferRef.current?.setVolume(1.0)
      }

      // Handle pause/loop at end
      if (playbackEndTime && time >= playbackEndTime) {
        if (loopMode === 'single') {
          // Loop: restart from buffered start time
          wavesurferRef.current?.setTime(playbackStartTime || 0)
          // Note: play() is not needed, already playing
        } else {
          // No loop: pause at end
          wavesurferRef.current?.pause()
        }
      }
    }

    wavesurferRef.current?.on('timeupdate', handleTimeUpdate)

    // Cleanup timeupdate listener
    return () => {
      wavesurferRef.current?.un('timeupdate', handleTimeUpdate)
    }

  }, [regions, isReady, onRegionClick, loopMode, isPlaying, wholeSongMode])

  // Apply playback rate when it changes
  useEffect(() => {
    if (wavesurferRef.current && isReady) {
      console.log('Setting playback rate to:', playbackRate)

      // Try WaveSurfer's setPlaybackRate method
      try {
        wavesurferRef.current.setPlaybackRate(playbackRate)
        console.log('Called wavesurfer.setPlaybackRate()')
      } catch (e) {
        console.error('setPlaybackRate failed:', e)
      }

      // Also set it directly on the media element as fallback
      const mediaElement = wavesurferRef.current.getMediaElement?.()
      if (mediaElement) {
        console.log('Setting playbackRate on media element:', playbackRate)
        mediaElement.playbackRate = playbackRate
        console.log('Media element playbackRate after setting:', mediaElement.playbackRate)
      } else {
        console.warn('No media element found')
      }

      // Verify final state
      const currentRate = wavesurferRef.current.getPlaybackRate?.()
      console.log('WaveSurfer getPlaybackRate():', currentRate)
    }
  }, [playbackRate, isReady])

  // Playback controls
  const togglePlayPause = useCallback(() => {
    wavesurferRef.current?.playPause()
    if (!hasPlayedOnce) {
      setHasPlayedOnce(true)
    }
  }, [hasPlayedOnce])

  const toggleLoopMode = useCallback(() => {
    setLoopMode((prev) => (prev === 'none' ? 'single' : 'none'))
  }, [])

  const decreaseSpeed = useCallback(() => {
    const speeds = [0.8, 0.9, 1.0, 1.1, 1.2]
    const currentIndex = speeds.indexOf(playbackRate)
    console.log('Decrease speed - current:', playbackRate, 'index:', currentIndex)
    if (currentIndex > 0) {
      const newSpeed = speeds[currentIndex - 1]
      console.log('Setting new speed to:', newSpeed)
      setPlaybackRate(newSpeed)
    }
  }, [playbackRate])

  const increaseSpeed = useCallback(() => {
    const speeds = [0.8, 0.9, 1.0, 1.1, 1.2]
    const currentIndex = speeds.indexOf(playbackRate)
    console.log('Increase speed - current:', playbackRate, 'index:', currentIndex)
    if (currentIndex < speeds.length - 1) {
      const newSpeed = speeds[currentIndex + 1]
      console.log('Setting new speed to:', newSpeed)
      setPlaybackRate(newSpeed)
    }
  }, [playbackRate])

  const stopPlayback = useCallback(() => {
    wavesurferRef.current?.pause()
    wavesurferRef.current?.setTime(0)
  }, [])

  const getSpeedLabel = (speed: number): string => {
    switch (speed) {
      case 0.8:
        return 'slowest'
      case 0.9:
        return 'slow'
      case 1.0:
        return 'normal speed'
      case 1.1:
        return 'fast'
      case 1.2:
        return 'fastest'
      default:
        return `${speed}x`
    }
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className={`terminal-border bg-black p-4 ${className}`}>
      {/* Animation Keyframes */}
      <style>{`
        @keyframes playTrianglePulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.25);
            opacity: 0.5;
          }
        }
        @keyframes playButtonGlow {
          0%, 100% {
            background-color: #000000;
          }
          50% {
            background-color: rgba(255, 255, 255, 0.5);
          }
        }
      `}</style>

      {/* Waveform Container */}
      <div
        ref={containerRef}
        className="w-full mb-4 rounded-sm overflow-hidden"
        style={{ minHeight: `${height}px` }}
      />

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlayPause}
          disabled={!isReady}
          className={`
            px-4 py-3 font-mono text-sm font-bold
            border-[3px] transition-all duration-200 flex items-center justify-center
            shadow-[2px_2px_0px_0px_rgba(255,255,255,0.3)]
            hover:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.4)]
            active:shadow-[1px_1px_0px_0px_rgba(255,255,255,0.2)]
            active:translate-x-[1px] active:translate-y-[1px]
            ${
              isReady
                ? 'border-gray-400 bg-black hover:border-white'
                : 'border-gray-700 bg-gray-900 cursor-not-allowed'
            }
          `}
          style={
            !hasPlayedOnce && !isPlaying && isReady
              ? { animation: 'playButtonGlow 3s ease-in-out infinite' }
              : undefined
          }
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <div className="flex gap-0.5">
              <div className="w-1 h-4 bg-white"></div>
              <div className="w-1 h-4 bg-white"></div>
            </div>
          ) : (
            <div
              className="w-0 h-0 border-l-[8px] border-l-[#00aa00] border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"
              style={{
                marginLeft: '2px',
                ...((!hasPlayedOnce && isReady) && { animation: 'playTrianglePulse 3s ease-in-out infinite' })
              }}
            ></div>
          )}
        </button>

        {/* Stop Button */}
        <button
          onClick={stopPlayback}
          disabled={!isReady}
          className={`
            px-4 py-3 font-mono text-sm font-bold
            border-[3px] transition-all duration-200 flex items-center justify-center
            shadow-[2px_2px_0px_0px_rgba(255,255,255,0.3)]
            hover:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.4)]
            active:shadow-[1px_1px_0px_0px_rgba(255,255,255,0.2)]
            active:translate-x-[1px] active:translate-y-[1px]
            ${
              isReady
                ? 'border-gray-400 bg-black text-white hover:border-white'
                : 'border-gray-700 bg-gray-900 text-gray-600 cursor-not-allowed'
            }
          `}
          title="Stop"
        >
          <Square size={14} fill="white" />
        </button>

        {/* Loop Button - Icon Only with Green Background When Active */}
        <button
          onClick={toggleLoopMode}
          disabled={!isReady}
          className={`
            px-4 py-3 font-mono text-xs font-bold
            border-[3px] transition-all duration-200 flex items-center justify-center
            shadow-[2px_2px_0px_0px_rgba(0,255,0,0.4)]
            hover:shadow-[4px_4px_0px_0px_rgba(0,255,0,0.5)]
            active:shadow-[1px_1px_0px_0px_rgba(0,255,0,0.3)]
            active:translate-x-[1px] active:translate-y-[1px]
            ${
              loopMode === 'single'
                ? 'border-terminal-green !bg-terminal-green !text-black hover:!bg-green-600'
                : 'border-gray-400 bg-black text-gray-400 hover:border-white hover:text-white'
            }
            ${!isReady && 'opacity-50 cursor-not-allowed'}
          `}
          title={loopMode === 'single' ? 'Loop enabled' : 'Loop disabled'}
        >
          <Repeat1 size={16} />
        </button>

        {/* Listening Mode Toggle - Clip vs Song */}
        {(onPlaybackModeToggle || onModeSelect) && (
          <div className="flex flex-col gap-1.5">
            {/* Label */}
            <div className="text-[10px] sm:text-xs text-gray-400 font-mono uppercase tracking-wide text-center">
              Listening Mode
            </div>
            {/* Toggle Button */}
            <button
              onClick={() => {
                // Toggle between vocab (clip) and wholeSong (song)
                const newMode = playbackMode === 'wholeSong' ? 'vocab' : 'wholeSong'
                onModeSelect ? onModeSelect(newMode) : onPlaybackModeToggle?.()
              }}
              disabled={!isReady}
              className={`
                px-4 py-3 font-mono text-xs sm:text-sm font-bold
                border-[3px] transition-all duration-200 flex items-center justify-center gap-2
                min-w-[80px] sm:min-w-[100px]
                border-terminal-green !bg-terminal-green !text-white
                shadow-[2px_2px_0px_0px_rgba(0,255,0,0.5)]
                hover:shadow-[4px_4px_0px_0px_rgba(0,255,0,0.6)]
                hover:!bg-green-600
                active:shadow-[1px_1px_0px_0px_rgba(0,255,0,0.4)]
                active:translate-x-[1px] active:translate-y-[1px]
                ${!isReady && 'opacity-50 cursor-not-allowed'}
              `}
              title={playbackMode === 'wholeSong' ? 'Currently: Song Mode (click to switch to Clip)' : 'Currently: Clip Mode (click to switch to Song)'}
            >
              {playbackMode === 'wholeSong' ? (
                <>
                  <Music size={14} />
                  <span>SONG</span>
                </>
              ) : (
                <>
                  <X size={14} />
                  <span>CLIP</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Speed Controls */}
        <div className="flex items-center gap-1">
          {/* Decrease Speed */}
          <button
            onClick={decreaseSpeed}
            disabled={!isReady || playbackRate === 0.8}
            className={`
              px-2 py-1 font-mono text-xs font-bold
              border-2 transition-all duration-200 flex items-center
              border-white bg-black text-white hover:bg-gray-800
              ${(!isReady || playbackRate === 0.8) && 'opacity-50 cursor-not-allowed'}
            `}
            title="Decrease speed"
          >
            <ChevronLeft size={12} strokeWidth={3} />
            <ChevronLeft size={12} strokeWidth={3} style={{ marginLeft: '-6px' }} />
          </button>

          {/* Current Speed Display */}
          <div
            className="px-3 py-1 font-mono text-xs font-bold border-2 bg-black text-white"
            style={{ borderColor: '#00aa00' }}
            title={`${playbackRate}x speed`}
          >
            {getSpeedLabel(playbackRate)}
          </div>

          {/* Increase Speed */}
          <button
            onClick={increaseSpeed}
            disabled={!isReady || playbackRate === 1.2}
            className={`
              px-2 py-1 font-mono text-xs font-bold
              border-2 transition-all duration-200 flex items-center
              border-white bg-black text-white hover:bg-gray-800
              ${(!isReady || playbackRate === 1.2) && 'opacity-50 cursor-not-allowed'}
            `}
            title="Increase speed"
          >
            <ChevronRight size={12} strokeWidth={3} />
            <ChevronRight size={12} strokeWidth={3} style={{ marginLeft: '-6px' }} />
          </button>
        </div>

        {/* Time Display */}
        <div className="flex-1 flex items-center gap-2 font-mono text-sm">
          <span className="text-cyber-cyan">{formatTime(currentTime)}</span>
          <span className="text-gray-600">/</span>
          <span className="text-gray-400">{formatTime(duration)}</span>
        </div>

        {/* Region Count */}
        {regions.length > 0 && (
          <div className="font-mono text-xs text-gray-500">
            {regions.length} flashcard{regions.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Loading State */}
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="font-mono text-cyber-cyan animate-pulse">
            [LOADING WAVEFORM...]
          </div>
        </div>
      )}
    </div>
  )
}
